/**
 * 🔄 새 버전 띠 (v2.226.0 · 2026-10-02 · 사용자 요청)
 *
 * 왜: 10-02 오전 배포 뒤 권애영 차장 폰이 **옛 화면에 39분 묶여** 고친 기능이 안 먹었다(앱을 지우고 다시 깔아도).
 *   원인 — 운영 nginx 가 index.html·sw.js 에 Cache-Control 을 안 붙여 브라우저가 옛 파일을 「아직 신선」하다고 보고 썼고,
 *   열어 둔 앱(한 화면 앱)은 화면 이동이 진짜 이동이 아니라 서비스 워커가 새 버전을 찾을 기회도 적었다.
 *
 * 무엇을: 앱을 열 때 · 앞으로 가져올 때 · 5분마다 서버 버전(/api/version)과 내 버전(__APP_VERSION__)을 비교 →
 *   다르면 맨 위에 「새 버전이 나왔습니다 [새로 고침]」. ⭐저절로 새로 고치지 않는다 — 수량·메모를 적는 중일 수 있다.
 * [새로 고침]: ① index.html 을 HTTP 캐시 무시하고 다시 받아 둠 ② 서비스 워커 새로 받기(update) → 바뀌면 다시 그림
 *   ③ 6초 안에 안 바뀌면 앱 저장소(CacheStorage)를 비우고 다시 그림. ⚠서비스 워커는 지우지 않는다(🔔 알림 구독이 끊긴다).
 */

import { useEffect, useRef, useState } from 'react';

const CHECK_EVERY_MS = 5 * 60_000;
const FIRST_CHECK_MS = 3_000;

async function fetchServerVersion(): Promise<string | null> {
  try {
    const res = await fetch('/api/version', { cache: 'no-store', credentials: 'omit' });
    if (!res.ok) return null;
    const j = (await res.json()) as { data?: { version?: unknown } };
    const v = j?.data?.version;
    return typeof v === 'string' && v ? v : null;
  } catch {
    return null;
  }
}

/** 새 버전으로 다시 그리기 — 위 머리 주석의 ①②③ */
export async function reloadToNewVersion(): Promise<void> {
  try { await fetch('/index.html', { cache: 'reload' }); } catch { /* 계속 */ }
  try {
    const sw = navigator.serviceWorker;
    const reg = sw ? await sw.getRegistration() : undefined;
    if (sw && reg) {
      const changed = new Promise<boolean>((resolve) => {
        const t = window.setTimeout(() => resolve(false), 6000);
        sw.addEventListener('controllerchange', () => { window.clearTimeout(t); resolve(true); }, { once: true });
      });
      await reg.update().catch(() => undefined);
      if (await changed) { window.location.reload(); return; }
    }
  } catch { /* 아래로 */ }
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* 그래도 다시 그린다 */ }
  window.location.reload();
}

export function NewVersionBanner() {
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nudged = useRef<string | null>(null);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      if (document.visibilityState !== 'visible') return;
      const v = await fetchServerVersion();
      if (!alive || !v) return;
      setServerVersion(v);
      // 다르면 서비스 워커도 미리 새로 받아 둔다(눌렀을 때 빨리 바뀌게) — 한 버전에 한 번
      if (v !== __APP_VERSION__ && nudged.current !== v) {
        nudged.current = v;
        try { (await navigator.serviceWorker?.getRegistration())?.update().catch(() => undefined); } catch { /* */ }
      }
    };
    const first = window.setTimeout(() => void check(), FIRST_CHECK_MS);
    const every = window.setInterval(() => void check(), CHECK_EVERY_MS);
    const onVis = () => { if (document.visibilityState === 'visible') void check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      alive = false;
      window.clearTimeout(first);
      window.clearInterval(every);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  if (!serverVersion || serverVersion === __APP_VERSION__ || dismissed === serverVersion) return null;
  return (
    <div data-new-version role="status"
      className="fixed top-0 inset-x-0 z-[100] bg-blue-700 text-white shadow-lg px-3 py-2 flex items-center gap-2 text-sm"
      style={{ paddingTop: 'calc(0.5rem + env(safe-area-inset-top))' }}>
      <span className="flex-1 min-w-0 leading-snug">
        🔄 <b>새 버전이 나왔습니다</b>
        <span className="text-blue-100 text-xs"> (v.{__APP_VERSION__} → v.{serverVersion}) · 적던 것을 저장한 뒤 눌러 주세요</span>
      </span>
      <button type="button" disabled={busy}
        onClick={() => { setBusy(true); void reloadToNewVersion(); }}
        className="shrink-0 px-3 min-h-[40px] rounded bg-white text-blue-800 font-bold disabled:opacity-60 active:bg-blue-50">
        {busy ? '받는 중…' : '새로 고침'}
      </button>
      <button type="button" onClick={() => setDismissed(serverVersion)} aria-label="띠 닫기"
        className="shrink-0 w-9 h-9 flex items-center justify-center rounded active:bg-blue-800">✕</button>
    </div>
  );
}
